import { useState } from "react"
import { useDropzone } from "react-dropzone"
import { FiFile, FiUploadCloud, FiX } from "react-icons/fi"
import Spinner from "react-bootstrap/Spinner"

import "./index.scss"
import useT from "utils/hooks/useT"

// Accepted document MIME types (PDF + Word .doc/.docx), mirroring the Librarian dropzone's
// validation. Exported so callers (Area 2 forskrifter, Area 4 QA reports) share one source.
export const DOCUMENT_ACCEPT = {
  "application/pdf": [".pdf"],
  "application/msword": [".doc"],
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [".docx"],
}

const ACCEPTED_MIME_TYPES = Object.keys(DOCUMENT_ACCEPT)

// DocumentUpload — a REUSABLE drag-drop single-file picker for controlled documents
// (PRD-003 Area 2 / SPEC-008), extracted from the Librarian dropzone so Area 2 (forskrifter)
// and Area 4 (QA report file) share the same UX + PDF/DOC/DOCX validation.
//
// It is a thin, controlled chooser: it validates the dropped/selected file's type, reports it
// up via `onFile(file)`, and shows the current selection + a rejected-files notice. It does NOT
// upload — the actual upload is routed through the caller (which calls the relevant slice thunk),
// so the same component serves any document-upload flow. Pass `uploading` to show a busy state.
//
// Props:
//   onFile(file|null)  — called with the accepted File (or null when cleared)
//   currentFileName    — name of the currently selected/stored file (controlled display)
//   uploading          — when true, shows a spinner and disables the dropzone
//   disabled           — disables interaction
export default function DocumentUpload({ onFile, currentFileName = null, uploading = false, disabled = false }) {
  const { t } = useT("common")
  const [rejected, setRejected] = useState(false)

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: DOCUMENT_ACCEPT,
    multiple: false,
    disabled: disabled || uploading,
    onDrop: (acceptedFiles, rejectedFiles) => {
      // Belt-and-braces: react-dropzone's `accept` already filters, but re-check the MIME type
      // (and surface a rejection notice) so the validation matches the Librarian dropzone.
      const valid = (acceptedFiles || []).filter((f) => ACCEPTED_MIME_TYPES.includes(f.type))
      const wasRejected = (rejectedFiles && rejectedFiles.length > 0) || valid.length < (acceptedFiles || []).length
      setRejected(wasRejected)
      if (valid.length > 0) onFile(valid[0])
    },
  })

  const handleClear = (e) => {
    e.stopPropagation()
    setRejected(false)
    onFile(null)
  }

  // The box content: busy spinner, the current selection (with clear + replace hint), or the
  // empty drop prompt. Pulled out so the render is a flat switch rather than a nested ternary.
  const renderContent = () => {
    if (uploading) {
      return (
        <div className="DocumentUpload-status">
          <Spinner animation="border" size="sm" /> {t("documentUploadUploading")}
        </div>
      )
    }
    if (currentFileName) {
      return (
        <div className="DocumentUpload-current">
          <FiFile className="DocumentUpload-fileicon" />
          <span className="DocumentUpload-filename">{currentFileName}</span>
          {!disabled && (
            <button
              type="button"
              className="DocumentUpload-clear"
              title={t("delete")}
              aria-label={t("delete")}
              onClick={handleClear}
            >
              <FiX />
            </button>
          )}
          <span className="DocumentUpload-replace">{t("documentUploadReplace")}</span>
        </div>
      )
    }
    return (
      <div className="DocumentUpload-prompt">
        <FiUploadCloud className="DocumentUpload-prompticon" />
        <span>{t("documentUploadDrop")}</span>
      </div>
    )
  }

  return (
    <div className="DocumentUpload">
      <div
        {...getRootProps({
          className:
            "DocumentUpload-box" +
            (isDragActive ? " is-drag" : "") +
            (disabled || uploading ? " is-disabled" : ""),
        })}
      >
        <input {...getInputProps()} />
        {renderContent()}
      </div>
      {rejected && <p className="DocumentUpload-rejected">{t("documentUploadRejected")}</p>}
    </div>
  )
}
